/** @format */
import { css } from 'styled-components';

import { CollapsibleAsideTitleColor, CollapsibleAsideVariant } from '.';

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type CollapsibleAsideStyleProps = {
    isOpen: boolean;
    variant: CollapsibleAsideVariant;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    $titleLocatedAtTop: boolean;
    $titleElementColor?: CollapsibleAsideTitleColor;
};

/**
 * Base styles for the collapsible aside component
 * */
/* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
const baseStyles = ({ $titleLocatedAtTop }: CollapsibleAsideStyleProps) => css`
    aside {
        background-color: var(--colors-surface-primary);

        .title {
            cursor: pointer;
            display: flex;
            align-items: center;

            svg {
                transition: 0.3s ease-in-out transform;
            }
        }

        .content-container,
        .content {
            overflow: hidden;
        }

        .content {
            padding-top: ${$titleLocatedAtTop ? 'var(--layout-spacing-24)' : 0};
        }
    }
`;
/* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */

/**
 * Variant styles for the collapsible aside component
 *
 * @param {CollapsibleAsideStyleProps} props The collapsible aside properties object
 * */
const variantStyles = ({ variant, isOpen }: CollapsibleAsideStyleProps) => {
    switch (variant) {
        case CollapsibleAsideVariant.INLINE:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                aside {
                    .collapsible-aside-title-container {
                        height: 3.6rem;
                        display: flex;
                        align-items: center;
                    }

                    .title {
                        display: inline-flex;
                        position: relative;

                        .toggle-button {
                            color: inherit;
                            position: absolute;
                            top: 50%;
                            transform: translateY(-50%);
                            fill: currentColor;
                            /* Width of the svg is actually 12px but has the left padding to sit flush next to the text */
                            width: 2rem;
                            padding-left: 0.8rem;
                            left: 100%;
                            height: 100%;

                            svg {
                                transform: scale(1, ${isOpen ? 1 : -1});
                                display: block;
                                width: 100%;
                                height: 100%;
                            }
                        }
                    }
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
        case CollapsibleAsideVariant.STANDALONE:
        default:
            return css`
                aside {
                    .title {
                        justify-content: space-between;

                        svg {
                            transform: rotate(90deg)
                                scale(${isOpen ? -1 : 1}, 1);
                        }
                    }
                }
            `;
    }
};

const chooseColor = (color: CollapsibleAsideTitleColor) => {
    switch (color) {
        case CollapsibleAsideTitleColor.MIDNIGHT:
            return 'var(--colors-text-secondary)';
        default:
            return 'inherit';
    }
};

const titleElementColorStyles = ({
    $titleElementColor,
}: CollapsibleAsideStyleProps) => {
    if (!$titleElementColor) return;

    return css`
        aside {
            display: contents;

            .title {
                &::after {
                    display: none;
                }
                span {
                    background-image: none !important;
                    margin-bottom: 0 !important;
                    padding-bottom: 0 !important;
                    -webkit-text-fill-color: initial;
                    color: ${chooseColor($titleElementColor)};
                    text-fill-color: initial;
                    text-decoration: underline;
                }
            }
        }
    `;
};

/**
 * The collapsible aside component styles
 *
 * @param {CollapsibleAsideStyleProps} props The collapsible aside properties object
 * */
export const styles = (props: CollapsibleAsideStyleProps) => css`
    ${baseStyles(props)}
    ${variantStyles(props)}
    ${titleElementColorStyles(props)}
`;
